<template>
  <main class="h-screen w-screen flex items-center justify-center bg-#f5f7fa px-4">
    <n-card class="max-w-560px w-full" :bordered="false">
      <n-result status="warning" :title="pageCopy.title" :description="pageCopy.description">
        <template #footer>
          <div class="flex flex-wrap justify-center gap-3">
            <n-button type="primary" @click="clearAndRetry">
              {{ pageCopy.primaryButtonText }}
            </n-button>
            <n-button @click="goHome">
              返回首页
            </n-button>
          </div>
        </template>
      </n-result>
    </n-card>
  </main>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { AUTH_ERROR_REASON, AUTH_ERROR_REASON_QUERY_KEY } from '@/router/auth-error-reason';
import { authStorage } from '@/services/auth/storage';

const route = useRoute();
const router = useRouter();

/**
 * 认证错误页展示文案。
 */
interface AuthErrorPageCopy {
  /** 错误页标题。 */
  title: string;

  /** 错误页说明。 */
  description: string;

  /** 主操作按钮文案。 */
  primaryButtonText: string;
}

/**
 * 当前认证错误原因。
 *
 * 统一读取共享 query key，确保路由守卫和页面展示不会因为字符串分叉而失配。
 */
const authErrorReason = computed(() => route.query[AUTH_ERROR_REASON_QUERY_KEY]);

/**
 * 根据认证错误原因生成页面文案。
 *
 * AuthError 是认证链路的统一兜底页，必须让不同错误原因展示不同行动建议，
 * 否则开发自动登录失败、外部登录配置错误与重定向循环会被用户混淆。
 */
const pageCopy = computed<AuthErrorPageCopy>(() => {
  if (authErrorReason.value === AUTH_ERROR_REASON.DevAutoLoginFailed) {
    return {
      title: '开发环境自动登录失败',
      description: '当前已开启 VITE_DEV_AUTO_LOGIN_ENABLED，但自动登录未能建立有效会话。请检查开发环境账号、密码、租户配置或后端认证服务返回。',
      primaryButtonText: '清除缓存并重试自动登录',
    };
  }

  if (authErrorReason.value === AUTH_ERROR_REASON.InvalidExternalAuthConfig) {
    return {
      title: '登录入口配置无效',
      description: '登录地址格式无效，请检查 VITE_EXTERNAL_AUTH_URL 后重新进入系统。',
      primaryButtonText: '重新检测',
    };
  }

  if (authErrorReason.value === AUTH_ERROR_REASON.MissingExternalAuthConfig) {
    return {
      title: '登录入口未配置',
      description: '登录已失效，但当前环境未配置 VITE_EXTERNAL_AUTH_URL，无法跳转到外部登录页。',
      primaryButtonText: '重新检测',
    };
  }

  return {
    title: '认证状态异常',
    description: '检测到认证重定向循环。请清除本地缓存后重试，或联系管理员检查登录系统配置。',
    primaryButtonText: '清除缓存并重试',
  };
});

/**
 * 清理本地认证缓存并重新进入根路径。
 *
 * 这里不主动跳转外部登录页，重试入口交给当前应用的会话初始化流程决定。
 */
function clearAndRetry(): void {
  authStorage.clearMetadata();
  sessionStorage.clear();
  window.location.href = '/';
}

/**
 * 返回应用根路径。
 *
 * 对开发自动登录失败来说，根路径会再次经过 devProjectEntryGuard，
 * 因此仍然能够保持“失败展示内部错误页”的语义。
 */
function goHome(): void {
  void router.push('/');
}
</script>
